"use client";

import { useState, useCallback, useMemo } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { AutocompleteSearchBar } from "@/components/search/autocomplete-search-bar";
import { CategoryPills } from "@/components/category-pills";
import { PageBackground } from "@/components/sections/page-background";
import { PageHeroWithSearch } from "@/components/sections/page-hero-with-search";
import { ProjectTile } from "@/app/[locale]/projects/_components/project-tile";
import { SimplePagination } from "@/components/pagination/simple-pagination";
import { TileGrid, TileGridSkeleton } from "@/components/tile-grid";
import { useProjectsGeneric } from "@/app/[locale]/projects/_api/use-project-queries";
import { getAllFieldsOfWork } from "@/app/[locale]/fields-of-work/_api/field-of-work-reads";

const PROJECTS_PER_PAGE = 27;

export function ProjectsPageContent() {
  const t = useTranslations("projects");
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const initialSearch = searchParams.get("search") ?? "";
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const selectedCategory = searchParams.get("category") ?? "all";
  const pageParam = searchParams.get("page");
  const currentPage = Math.max(1, pageParam ? parseInt(pageParam, 10) || 1 : 1);

  const handleSubmit = useCallback(
    (value: string) => {
      setSearchQuery(value);
      const params = new URLSearchParams();
      if (value) params.set("search", value);
      if (selectedCategory !== "all") params.set("category", selectedCategory);
      params.set("page", "1");
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [selectedCategory, router, pathname],
  );

  const handleCategorySelect = useCallback(
    (categoryId: string) => {
      const params = new URLSearchParams();
      if (searchQuery) params.set("search", searchQuery);
      if (categoryId !== "all") params.set("category", categoryId);
      params.set("page", "1");
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [searchQuery, router, pathname],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const params = new URLSearchParams();
      if (searchQuery) params.set("search", searchQuery);
      if (selectedCategory !== "all") params.set("category", selectedCategory);
      if (page > 1) params.set("page", String(page));
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [searchQuery, selectedCategory, router, pathname],
  );

  // Fields of work for categories scrollbar
  const { data: fieldsOfWork = [] } = useQuery({
    queryKey: ["fields-of-work"],
    queryFn: () => getAllFieldsOfWork(),
    staleTime: 30 * 60_000,
  });

  const categoryItems = useMemo(
    () => [
      { id: "all", label: t("browser.all"), icon: "earth", color: "var(--primary)" },
      ...fieldsOfWork.map((fow) => ({ id: fow.id, label: fow.title, icon: fow.icon, color: fow.color })),
    ],
    [fieldsOfWork, t],
  );

  // Projects data
  const { data: projectsPage, isLoading } = useProjectsGeneric({
    fieldOfWorkId: selectedCategory === "all" ? undefined : selectedCategory,
    searchTerm: searchQuery || undefined,
    page: currentPage - 1,
    size: PROJECTS_PER_PAGE,
  });

  const projects = projectsPage?.content ?? [];
  const totalPages = projectsPage?.totalPages ?? 0;

  return (
    <PageBackground>
      <PageHeroWithSearch
        title={t("page.hero.headline")}
        subtitle={t("page.hero.subtitle")}
      >
        <AutocompleteSearchBar
          sources={["projects"]}
          onSubmit={handleSubmit}
          defaultValue={initialSearch}
          placeholder={t("page.hero.searchPlaceholder")}
        />
      </PageHeroWithSearch>

      {/* Categories */}
      <section className="py-4">
        <div className="max-w-7xl mx-auto">
          <CategoryPills
            items={categoryItems}
            activeId={selectedCategory}
            onSelect={handleCategorySelect}
          />
        </div>
      </section>

      {/* Grid content */}
      <div className="max-w-7xl mx-auto space-y-6 pb-8">
        {isLoading ? (
          <TileGridSkeleton count={10}  />
        ) : projects.length > 0 ? (
          <>
            <TileGrid >
              {projects.map((project) => (
                <ProjectTile key={project.id} project={project} />
              ))}
            </TileGrid>

            <SimplePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <div className="text-center py-10">
            <p className="text-muted-foreground">{t("page.noProject")}</p>
          </div>
        )}
      </div>
    </PageBackground>
  );
}
